import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Section, Cell, Button, Spinner, Caption, Select, Textarea,
} from "@telegram-apps/telegram-ui";
import { api, openAuthedFile } from "../../lib/api.js";
import { usePageBack } from "../../lib/telegram.js";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ru-RU"); } catch { return iso; }
}

function userLabel(d) {
  const name = [d.first_name, d.last_name].filter(Boolean).join(" ");
  if (name) return d.tg_username ? `${name} (@${d.tg_username})` : name;
  if (d.tg_username) return `@${d.tg_username}`;
  return `tg:${d.tg_user_id}`;
}

export default function DocumentsQueue() {
  const navigate = useNavigate();
  usePageBack("/superadmin");
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "pending";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  function reload() {
    setData(null);
    api(`/api/v1/superadmin/documents?status=${encodeURIComponent(status)}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }
  useEffect(reload, [status]);

  async function approve(docId) {
    try {
      await api(`/api/v1/superadmin/documents/${docId}/review`, {
        method: "POST", body: JSON.stringify({ status: "verified" }),
      });
      reload();
    } catch (e) { setError(e.message); }
  }

  async function reject() {
    if (!rejecting?.id) return;
    if (!rejecting.reason?.trim()) { setError("Укажите причину отказа."); return; }
    try {
      await api(`/api/v1/superadmin/documents/${rejecting.id}/review`, {
        method: "POST",
        body: JSON.stringify({ status: "rejected", reason: rejecting.reason }),
      });
      setRejecting(null);
      reload();
    } catch (e) { setError(e.message); }
  }

  const total = data?.documents?.length ?? 0;
  const tabs = [
    { code: "pending",  label: "На проверке", tone: "warn" },
    { code: "verified", label: "Подтверждены", tone: "ok" },
    { code: "rejected", label: "Отклонены", tone: "danger" },
  ];

  return (
    <>
      <section className="portal-card">
        <header className="portal-card__header">
          <h2 className="portal-card__title">Очередь документов</h2>
          {data && <span className="portal-card__hint">Найдено: {total}</span>}
        </header>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {tabs.map((t) => {
            const active = status === t.code;
            return (
              <button
                key={t.code}
                type="button"
                className="portal-pager__btn"
                style={active ? {
                  background: "var(--portal-text)", color: "#fff", borderColor: "var(--portal-text)",
                } : undefined}
                onClick={() => setParams({ status: t.code }, { replace: true })}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      {error && <div className="portal-card"><p className="portal-empty">Ошибка: {error}</p></div>}

      <Section header={data ? `Найдено: ${data.documents.length}` : "Загрузка..."}>
        {!data && !error && <Cell><Spinner size="m" /></Cell>}
        {data?.documents?.map((d) => (
          <Cell
            key={d.id}
            multiline
            subtitle={
              `${d.kind} · ${formatDate(d.uploaded_at)}` +
              (d.expires_on ? ` · истекает ${d.expires_on}` : "") +
              (d.size_bytes ? ` · ${Math.round(d.size_bytes / 1024)} КБ` : "")
            }
          >
            <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/superadmin/users/${d.user_id}`); }}>
              {userLabel(d)}
            </a>
            <br />
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <Button
                size="s"
                mode="plain"
                onClick={() => openAuthedFile(`/api/v1/superadmin/documents/${d.id}/file`).catch((e) => setError(e.message))}
              >📎 Открыть</Button>
              {status === "pending" && (
                <>
                  <Button size="s" onClick={() => approve(d.id)}>✓ Подтвердить</Button>
                  <Button
                    size="s" mode="outline"
                    onClick={() => setRejecting({ id: d.id, reason: "" })}
                  >✕ Отклонить</Button>
                </>
              )}
              {d.rejection_reason && <Caption level="1">Причина: {d.rejection_reason}</Caption>}
            </div>

            {rejecting?.id === d.id && (
              <div style={{ marginTop: 8 }}>
                <Textarea
                  rows={2}
                  placeholder="Причина отказа (видна пилоту)"
                  value={rejecting.reason}
                  onChange={(e) => setRejecting({ ...rejecting, reason: e.target.value })}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button size="s" onClick={reject}>Подтвердить отказ</Button>
                  <Button size="s" mode="plain" onClick={() => setRejecting(null)}>Отмена</Button>
                </div>
              </div>
            )}
          </Cell>
        ))}
        {data && data.documents.length === 0 && (
          <Cell><Caption level="1">Документов нет.</Caption></Cell>
        )}
      </Section>
    </>
  );
}
